import React, { useRef, useCallback, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useInView } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { Link } from "react-scroll";
import { BsArrowDown } from "react-icons/bs";
import avatar from "../../assets/avatar.webp";
import { SITE } from "../../constants";
import OptimizedImage from "../ui/OptimizedImage";
import SocialLinks from "../ui/SocialLinks";
import PortfolioModal from "../ui/PortfolioModal";

const EASE_EXPO = [0.22, 1, 0.36, 1];
const AVATAR_SIZE = 288;
const RING_PAD = 22;

/* ─── per-letter blur reveal ──────────────────── */
const LetterReveal = ({ text, baseDelay = 0, active }) =>
  text.split("").map((char, i) => (
    <motion.span
      key={i}
      style={{ display: "inline-block" }}
      initial={{ opacity: 0, y: 32, filter: "blur(10px)" }}
      animate={
        active
          ? { opacity: 1, y: 0, filter: "blur(0px)" }
          : { opacity: 0, y: 32, filter: "blur(10px)" }
      }
      transition={{
        duration: active ? 0.65 : 0.3,
        ease: EASE_EXPO,
        delay: active ? baseDelay + i * 0.045 : 0,
      }}
    >
      {char}
    </motion.span>
  ));

/* ─── rotating SVG ring ───────────────────────── */
const OrbitalRing = ({ size, dashArray, stroke, duration, reverse = false }) => (
  <div
    className={`hero-spin${reverse ? " hero-spin--reverse" : ""}`}
    style={{ position: "absolute", inset: -(size - AVATAR_SIZE) / 2, zIndex: 0, animationDuration: `${duration}s` }}
    aria-hidden="true"
  >
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - 2}
        stroke={stroke}
        strokeWidth="1"
        strokeDasharray={dashArray}
      />
    </svg>
  </div>
);

/* ─── HERO SECTION ────────────────────────────── */
const HeroSection = () => {
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: false, amount: 0.35 });
  const mX = useMotionValue(0.5);
  const mY = useMotionValue(0.5);
  const spX = useSpring(mX, { stiffness: 45, damping: 20 });
  const spY = useSpring(mY, { stiffness: 45, damping: 20 });

  /* aurora parallax — each blob moves at different rate/direction */
  const b1x = useTransform(spX, [0, 1], [-55, 55]);
  const b1y = useTransform(spY, [0, 1], [-38, 38]);
  const b2x = useTransform(spX, [0, 1], [35, -35]);
  const b2y = useTransform(spY, [0, 1], [25, -25]);
  /* avatar counter-moves (deeper parallax layer) */
  const avX = useTransform(spX, [0, 1], [16, -16]);
  const avY = useTransform(spY, [0, 1], [9, -9]);

  const onMouseMove = useCallback(
    (e) => {
      const r = sectionRef.current?.getBoundingClientRect();
      if (!r) return;
      mX.set((e.clientX - r.left) / r.width);
      mY.set((e.clientY - r.top) / r.height);
    },
    [mX, mY]
  );

  return (
    <section
      ref={sectionRef}
      className="hero-section"
      id="home"
      data-tour="home"
      onMouseMove={onMouseMove}
      style={{ position: "relative" }}
    >
      {/* ── Aurora blobs (clipped so they don't scroll the page) ── */}
      <div
        style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}
        aria-hidden="true"
      >
        <motion.div
          style={{
            position: "absolute",
            width: 660,
            height: 660,
            top: "-22%",
            right: "-8%",
            x: b1x,
            y: b1y,
          }}
        >
          <div
            className="hero-aurora hero-aurora--violet"
            style={{
              background: "radial-gradient(circle, rgba(129,140,248,0.22) 0%, transparent 62%)",
              filter: "blur(90px)",
            }}
          />
        </motion.div>
        <motion.div
          style={{
            position: "absolute",
            width: 500,
            height: 500,
            bottom: "-18%",
            left: "-6%",
            x: b2x,
            y: b2y,
          }}
        >
          <div
            className="hero-aurora hero-aurora--cyan"
            style={{
              background: "radial-gradient(circle, rgba(34,211,238,0.14) 0%, transparent 62%)",
              filter: "blur(80px)",
            }}
          />
        </motion.div>
        {/* mid-tone indigo accent */}
        <div
          style={{
            position: "absolute",
            width: 380,
            height: 380,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(79,70,229,0.09) 0%, transparent 65%)",
            filter: "blur(70px)",
            top: "40%",
            left: "40%",
            transform: "translate(-50%,-50%)",
          }}
        />
      </div>

      <div className="container w-full relative" style={{ zIndex: 1 }}>

        {/* ── Mobile hero avatar + status ── */}
        <motion.div
          className="flex flex-col items-center mb-8 lg:hidden"
          initial={{ opacity: 0, scale: 0.88, y: 12 }}
          animate={
            inView
              ? { opacity: 1, scale: 1, y: 0 }
              : { opacity: 0, scale: 0.88, y: 12 }
          }
          transition={{
            duration: inView ? 0.75 : 0.3,
            ease: EASE_EXPO,
            delay: inView ? 0.15 : 0,
          }}
        >
          {/* Avatar */}
          <div style={{ position: "relative", width: 172, height: 172 }}>
            {/* outer rotating ring */}
            <div className="hero-spin" style={{ position: "absolute", inset: -18, animationDuration: "26s" }}>
              <svg width={208} height={208} viewBox="0 0 208 208" fill="none">
                <circle cx={104} cy={104} r={101} stroke="rgba(129,140,248,0.22)" strokeWidth="1" strokeDasharray="6 12" />
              </svg>
            </div>
            {/* inner counter-rotating ring */}
            <div className="hero-spin hero-spin--reverse" style={{ position: "absolute", inset: -8, animationDuration: "18s" }}>
              <svg width={188} height={188} viewBox="0 0 188 188" fill="none">
                <circle cx={94} cy={94} r={91} stroke="rgba(34,211,238,0.14)" strokeWidth="1" strokeDasharray="3 16" />
              </svg>
            </div>
            {/* pulse ring */}
            <div
              className="hero-pulse hero-pulse--mobile"
              style={{ position: "absolute", inset: -3, borderRadius: "50%", border: "1px solid rgba(129,140,248,0.28)" }}
            />
            <div
              style={{
                width: 172, height: 172, borderRadius: "50%",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(0,0,0,0.35)",
              }}
            >
              <OptimizedImage
                src={avatar}
                alt={SITE.name}
                priority
                style={{
                  width: "100%", height: "100%",
                  display: "block", objectFit: "cover", objectPosition: "center center",
                }}
              />
            </div>
            {/* status dot */}
            <span
              style={{
                position: "absolute", bottom: 8, right: 8,
                width: 16, height: 16, borderRadius: "50%",
                background: "#34d399",
                border: "2.5px solid #050505",
                boxShadow: "0 0 10px rgba(52,211,153,0.6)",
              }}
              aria-label="Available for work"
            />
          </div>

          {/* Status pill below avatar */}
          <motion.div
            style={{
              marginTop: 14,
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 14px",
              borderRadius: 999,
              background: "rgba(7,7,11,0.85)",
              border: "1px solid rgba(52,211,153,0.18)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
            transition={{
              duration: inView ? 0.5 : 0.3,
              ease: EASE_EXPO,
              delay: inView ? 0.55 : 0,
            }}
          >
            <span
              style={{
                width: 6, height: 6, borderRadius: "50%",
                background: "#34d399",
                boxShadow: "0 0 8px rgba(52,211,153,0.55)",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 10, letterSpacing: "0.14em",
                color: "rgba(255,255,255,0.46)",
              }}
            >
              {SITE.availability}
            </span>
          </motion.div>
        </motion.div>

        {/* ── Main flex row ── */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-14 lg:gap-16">

          {/* ══ LEFT — text ══ */}
          <div className="flex-1 min-w-0">

            {/* NAME — letter-by-letter reveal */}
            <h1
              className="text-display"
              style={{ marginBottom: 6, letterSpacing: "-0.03em", lineHeight: 0.95 }}
            >
              {/* "Mario" — white letters */}
              <span style={{ display: "block" }}>
                <LetterReveal text="Mario" baseDelay={0.32} active={inView} />
              </span>

              {/* "Nassar" — gradient, whole-word reveal */}
              <motion.span
                className="text-gradient"
                style={{ display: "block" }}
                initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                animate={
                  inView
                    ? { opacity: 1, y: 0, filter: "blur(0px)" }
                    : { opacity: 0, y: 40, filter: "blur(12px)" }
                }
                transition={{
                  duration: inView ? 0.75 : 0.3,
                  ease: EASE_EXPO,
                  delay: inView ? 0.72 : 0,
                }}
              >
                Nassar
              </motion.span>
            </h1>

            {/* role */}
            <motion.p
              className="hero-role mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: inView ? 1 : 0 }}
              transition={{ duration: inView ? 0.5 : 0.3, delay: inView ? 1.0 : 0 }}
            >
              <span className="text-role-prefix">I&apos;m a</span>
              <TypeAnimation
                sequence={SITE.roles.flatMap((r) => [r, 2200])}
                speed={55}
                wrapper="span"
                repeat={Infinity}
                className="text-role"
              />
            </motion.p>

            {/* tagline */}
            <motion.p
              className="text-body text-white/40 mb-8 max-w-[420px]"
              initial={{ opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
              transition={{
                duration: inView ? 0.6 : 0.3,
                ease: EASE_EXPO,
                delay: inView ? 1.1 : 0,
              }}
            >
              {SITE.tagline}
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8"
              initial={{ opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
              transition={{
                duration: inView ? 0.6 : 0.3,
                ease: EASE_EXPO,
                delay: inView ? 1.2 : 0,
              }}
            >
              <Link to="contact" smooth className="cursor-pointer w-full sm:w-auto">
                <button type="button" className="btn btn-primary w-full sm:w-auto justify-center" style={{ minHeight: 48, fontSize: 14 }}>
                  Contact me
                </button>
              </Link>
              <button
                type="button"
                onClick={() => setPortfolioOpen(true)}
                className="btn btn-outline w-full sm:w-auto justify-center"
                style={{ minHeight: 48, fontSize: 14 }}
              >
                My Portfolio
              </button>
            </motion.div>

            {/* socials */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: inView ? 1 : 0 }}
              transition={{ duration: inView ? 0.5 : 0.3, delay: inView ? 1.35 : 0 }}
            >
              <SocialLinks />
            </motion.div>
          </div>

          {/* ══ RIGHT — avatar lens, desktop only ══ */}
          <motion.div
            className="hidden lg:block flex-shrink-0"
            style={{ width: 400, position: "relative", x: avX, y: avY }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={
              inView
                ? { opacity: 1, scale: 1 }
                : { opacity: 0, scale: 0.9 }
            }
            transition={{
              duration: inView ? 0.9 : 0.3,
              ease: EASE_EXPO,
              delay: inView ? 0.45 : 0,
            }}
          >
            {/* center the avatar in the 400px column */}
            <div
              style={{
                position: "relative",
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                margin: "0 auto",
              }}
            >
              {/* outer ring — slow CW */}
              <OrbitalRing
                size={AVATAR_SIZE + (RING_PAD + 14) * 2}
                dashArray="7 14"
                stroke="rgba(129,140,248,0.26)"
                duration={28}
              />

              {/* inner ring — faster CCW */}
              <OrbitalRing
                size={AVATAR_SIZE + RING_PAD * 2}
                dashArray="3 18"
                stroke="rgba(34,211,238,0.16)"
                duration={18}
                reverse
              />

              {/* pulsing static halo */}
              <div
                className="hero-pulse hero-pulse--desktop"
                style={{
                  position: "absolute",
                  inset: -5,
                  borderRadius: "50%",
                  border: "1px solid rgba(129,140,248,0.32)",
                  zIndex: 0,
                }}
              />

              {/* avatar image */}
              <div
                style={{
                  position: "relative",
                  width: AVATAR_SIZE,
                  height: AVATAR_SIZE,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(0,0,0,0.4)",
                  zIndex: 1,
                }}
              >
                <OptimizedImage
                  src={avatar}
                  alt={SITE.name}
                  priority
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "cover",
                    objectPosition: "center center",
                    filter: "brightness(0.85) saturate(0.88)",
                  }}
                />
                {/* inner shadow vignette */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    boxShadow: "inset 0 0 70px rgba(0,0,0,0.55)",
                    pointerEvents: "none",
                  }}
                  aria-hidden="true"
                />
              </div>

              {/* status pill — bottom center */}
              <motion.div
                style={{
                  position: "absolute",
                  bottom: -20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "8px 16px",
                  borderRadius: 999,
                  background: "rgba(7,7,11,0.90)",
                  border: "1px solid rgba(52,211,153,0.20)",
                  backdropFilter: "blur(22px)",
                  WebkitBackdropFilter: "blur(22px)",
                  boxShadow: "0 10px 32px rgba(0,0,0,0.5)",
                  zIndex: 20,
                  whiteSpace: "nowrap",
                }}
                initial={{ opacity: 0, y: 10 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{
                  duration: inView ? 0.55 : 0.3,
                  ease: EASE_EXPO,
                  delay: inView ? 1.25 : 0,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "#34d399",
                    boxShadow: "0 0 9px rgba(52,211,153,0.55)",
                    flexShrink: 0,
                    animation: "pulse 2.4s ease-in-out infinite",
                  }}
                />
                <span
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    color: "rgba(255,255,255,0.48)",
                  }}
                >
                  {SITE.availability}
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* scroll hint */}
        <motion.div
          className="flex items-center gap-3 mt-6 lg:mt-10 justify-center lg:justify-start"
          initial={{ opacity: 0 }}
          animate={{ opacity: inView ? 1 : 0 }}
          transition={{ duration: inView ? 0.5 : 0.3, delay: inView ? 1.6 : 0 }}
        >
          <div className="text-white/20 hero-bob">
            <BsArrowDown size={14} />
          </div>
          <span className="scroll-hint">Scroll to explore</span>
        </motion.div>
      </div>

      <PortfolioModal isOpen={portfolioOpen} onClose={() => setPortfolioOpen(false)} />
    </section>
  );
};

export default HeroSection;
